<script lang="ts">
  import type { Api, VendorGroup } from "../../lib/api";
  import { ApiError } from "../../lib/api";
  import Card from "../../lib/ui/Card.svelte";
  import EmptyState from "../../lib/ui/EmptyState.svelte";
  import Banner from "../../lib/ui/Banner.svelte";
  import { Button as UiButton, Checkbox, Input, dialogs } from "@price-check/ui";
  import { addGroup, deleteGroup, makeGroup, renameGroup, toggleMember } from "../../lib/groups";

  let {
    api,
    orgId,
    suppliers,
  }: { api: Api; orgId: string; suppliers: { id: string; name: string }[] } = $props();

  let groups = $state<VendorGroup[]>([]);
  let version = $state(0);
  let savedJson = $state("[]");
  let newName = $state("");
  let error = $state("");
  let saving = $state(false);
  const dirty = $derived(JSON.stringify(groups) !== savedJson);

  async function load() {
    try {
      const r = await api.orgGroups(orgId);
      groups = Array.isArray(r?.groups) ? r.groups : [];
      version = typeof r?.version === "number" ? r.version : 0;
      savedJson = JSON.stringify(groups);
    } catch {
      groups = [];
      version = 0;
      savedJson = "[]";
    }
  }
  $effect(() => {
    void load();
  });

  function add(e: Event) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    groups = addGroup(groups, makeGroup(name));
    newName = "";
  }
  async function rename(id: string) {
    const current = groups.find((g) => g.id === id);
    const name = (
      await dialogs.prompt({
        title: "Rename group",
        defaultValue: current?.name ?? "",
        confirmLabel: "Rename",
      })
    )?.trim();
    if (!name) return;
    groups = renameGroup(groups, id, name);
  }
  function remove(id: string) {
    groups = deleteGroup(groups, id);
  }
  function toggle(id: string, supplierId: string) {
    groups = toggleMember(groups, id, supplierId);
  }
  async function save() {
    saving = true;
    error = "";
    try {
      const r = await api.saveOrgGroups(orgId, groups, version);
      groups = r.groups;
      version = r.version;
      savedJson = JSON.stringify(r.groups);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const reload = await dialogs.confirm({
          title: "Groups changed elsewhere",
          message:
            "This org's groups were saved by someone else since you loaded them. " +
            "Reload the latest version? Your unsaved edits here will be discarded and need re-applying.",
          confirmLabel: "Reload",
        });
        if (reload) await load();
      } else {
        error = e instanceof Error ? e.message : "could not save groups";
      }
    } finally {
      saving = false;
    }
  }
</script>

<Card title="Vendor groups" subtitle="Curated supplier sets — editable here and in the desktop app.">
  <form class="flex gap-2 mb-3" onsubmit={add}>
    <Input class="flex-1 min-w-0" placeholder="New group name" bind:value={newName} />
    <UiButton type="submit" size="sm" disabled={!newName.trim()}>Add group</UiButton>
  </form>
  {#if error}<div class="mb-3"><Banner tone="danger">{error}</Banner></div>{/if}

  {#if groups.length === 0}
    <EmptyState
      icon="◫"
      title="No vendor groups"
      hint="Add a group above to scope searches to a supplier subset."
    />
  {:else}
    <ul class="list-none m-0 p-0">
      {#each groups as g (g.id)}
        <li class="py-2.5 text-sm border-b border-border last:border-b-0">
          <div class="flex items-center gap-2">
            <span class="font-semibold">{g.name}</span>
            <span class="text-[11px] text-muted">
              {g.supplierIds.length}
              {g.supplierIds.length === 1 ? "supplier" : "suppliers"}
            </span>
            <span class="ml-auto flex gap-1">
              <UiButton variant="ghost" size="sm" onclick={() => rename(g.id)}>Rename</UiButton>
              <UiButton variant="destructive-ghost" size="sm" onclick={() => remove(g.id)}>
                Delete
              </UiButton>
            </span>
          </div>
          {#if suppliers.length === 0}
            <p class="m-0 mt-1.5 text-[11px] italic text-muted">
              No suppliers enabled for this org yet.
            </p>
          {:else}
            <div class="mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5">
              {#each suppliers as s (s.id)}
                <label class="flex items-center gap-1.5 text-xs cursor-pointer">
                  <Checkbox
                    checked={g.supplierIds.includes(s.id)}
                    onCheckedChange={() => toggle(g.id, s.id)}
                    aria-label="{s.name} in {g.name}"
                  />
                  {s.name}
                </label>
              {/each}
            </div>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}

  <div class="mt-3 flex justify-end">
    <UiButton size="sm" disabled={!dirty || saving} onclick={save}>
      {saving ? "Saving…" : "Save changes"}
    </UiButton>
  </div>
</Card>
